'use client';

import { useEffect, useState } from 'react';

/**
 * Returns a value which reflects changes to the provided `value`,
 * but only after the specified `delay` elapses without further changes to
 * the provided `value`. Each change resets the `delay` timer.
 *
 * @param value Value to debounce
 * @param delay Delay of debounce to apply
 *
 * @returns The debounced value
 */
export function useDebounce<T>(
  value: T,
  delay: number | ((value: T) => number),
): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedValue(value),
      typeof delay === 'function' ? delay(value) : delay,
    );

    return () => clearTimeout(timeout);
  }, [value, delay]);

  return debouncedValue;
}
